import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import './QRPage.css';
import AppLinks from './components/AppLinks';
import HowItWorks from './components/HowItWorks';
import { HOW_IT_WORKS } from './components/howItWorksContent';
import logoImg from './images/V2_DVF_Logo_Transparent_web.png';

const PRESETS = [
  { label: 'Portfolio',  key: 'portfolio' },
  { label: 'Card Page',  key: 'card'      },
  { label: 'LinkedIn',   key: 'custom', value: 'https://linkedin.com/in/dmitriy-filimonov/' },
  { label: 'GitHub',     key: 'custom', value: 'https://github.com/dtech982'                },
  { label: 'Email',      key: 'custom', value: 'mailto:dmitriyfilimonov33@gmail.com'        },
  { label: 'Custom',     key: 'custom', value: ''                                           },
];

// Palette presets
const COLOR_PRESETS = [
  { label: 'Portfolio',    dark: '#1A2744', light: '#FAF6EC', transparent: false },
  { label: 'Navy on White',dark: '#1A2744', light: '#FFFFFF', transparent: false },
  { label: 'Gold on Navy', dark: '#C9A84C', light: '#1A2744', transparent: false },
  { label: 'Black on White',dark:'#000000', light: '#FFFFFF', transparent: false },
  { label: 'Transparent',  dark: '#1A2744', light: '#FFFFFF', transparent: true  },
];

function resolveURL(preset) {
  const origin = window.location.origin;
  if (preset.key === 'portfolio') return origin;
  if (preset.key === 'card')      return `${origin}?card`;
  return preset.value || origin;
}

export default function QRPage() {
  const [selectedPreset, setSelectedPreset] = useState(PRESETS[0]);
  const [customURL, setCustomURL]           = useState('');
  const [qrDataURL, setQrDataURL]           = useState('');
  const [activeURL, setActiveURL]           = useState('');
  const [copied, setCopied]                 = useState(false);
  const [size, setSize]                     = useState(400);
  const [qrError, setQrError]               = useState(null);

  // Color state
  const [darkColor, setDarkColor]           = useState('#1A2744');
  const [lightColor, setLightColor]         = useState('#FAF6EC');
  const [transparent, setTransparent]       = useState(false);
  const [activeColorPreset, setActiveColorPreset] = useState('Portfolio');

  const getURL = useCallback(() => {
    if (selectedPreset.key === 'custom') return customURL;
    return resolveURL(selectedPreset);
  }, [selectedPreset, customURL]);

  // Generate QR -- handles transparent background via canvas manipulation
  useEffect(() => {
    const url = getURL();
    if (!url) { setQrDataURL(''); setActiveURL(''); return; }
    setActiveURL(url);

    setQrError(null);
    if (transparent) {
      QRCode.toCanvas(url, {
        width: size,
        margin: 2,
        color: { dark: darkColor, light: '#FFFFFF' },
        errorCorrectionLevel: 'H',
      }).then(canvas => {
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > 200 && data[i+1] > 200 && data[i+2] > 200) {
            data[i+3] = 0;
          }
        }
        ctx.putImageData(imageData, 0, 0);
        setQrDataURL(canvas.toDataURL('image/png'));
      }).catch(err => {
        setQrError('Failed to generate QR code. Check the URL and try again.');
        setQrDataURL('');
      });
    } else {
      QRCode.toDataURL(url, {
        width: size,
        margin: 2,
        color: { dark: darkColor, light: lightColor },
        errorCorrectionLevel: 'H',
      }).then(setQrDataURL).catch(err => {
        setQrError('Failed to generate QR code. Check the URL and try again.');
        setQrDataURL('');
      });
    }
  }, [getURL, size, darkColor, lightColor, transparent]);

  const applyColorPreset = (preset) => {
    setDarkColor(preset.dark);
    setLightColor(preset.light);
    setTransparent(preset.transparent);
    setActiveColorPreset(preset.label);
  };

  const handleDownload = () => {
    if (!qrDataURL) return;
    const a = document.createElement('a');
    a.href = qrDataURL;
    const ext = 'png';
    a.download = `DVF-QR-${selectedPreset.label.replace(/\s+/g, '-')}.${ext}`;
    a.click();
  };

  const handleCopy = () => {
    if (!activeURL) return;
    navigator.clipboard.writeText(activeURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="qr-page">
      <div className="qr-inner">

        {/* Masthead */}
        <div className="qr-masthead">
          <div className="qr-rule" />
          <p className="qr-eyebrow">D · V · F</p>
          <img src={logoImg} alt="DVF Logo" className="qr-logo" />
          <h1 className="qr-title">QR Generator</h1>
          <div className="qr-rule" />
        </div>

        <div className="qr-layout">

          {/* LEFT -- controls */}
          <div className="qr-controls">

            {/* Destination presets */}
            <div className="qr-control-group">
              <p className="qr-control-label">Destination</p>
              <div className="qr-presets">
                {PRESETS.map((p, i) => (
                  <button
                    key={i}
                    className={`qr-preset-btn ${selectedPreset === p ? 'qr-preset-btn--active' : ''}`}
                    onClick={() => {
                      setSelectedPreset(p);
                      if (p.key !== 'custom') setCustomURL('');
                      else if (p.value) setCustomURL(p.value);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* URL input / display */}
            <div className="qr-control-group">
              <p className="qr-control-label">
                {selectedPreset.key === 'custom' ? 'Enter URL' : 'Generated URL'}
              </p>
              {selectedPreset.key === 'custom' ? (
                <input
                  className="qr-url-input"
                  type="url"
                  value={customURL}
                  onChange={e => setCustomURL(e.target.value)}
                  placeholder="https://..."
                />
              ) : (
                <div className="qr-url-display">
                  <span>{activeURL || '...'}</span>
                  <button className="qr-copy-btn" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              )}
            </div>

            {/* Color presets */}
            <div className="qr-control-group">
              <p className="qr-control-label">Color Preset</p>
              <div className="qr-presets">
                {COLOR_PRESETS.map((p, i) => (
                  <button
                    key={i}
                    className={`qr-preset-btn ${activeColorPreset === p.label ? 'qr-preset-btn--active' : ''}`}
                    onClick={() => applyColorPreset(p)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom color pickers */}
            <div className="qr-control-group">
              <p className="qr-control-label">Custom Colors</p>
              <div className="qr-color-row">
                <div className="qr-color-pick">
                  <label className="qr-color-label">QR Color</label>
                  <div className="qr-color-input-wrap">
                    <input
                      type="color"
                      value={darkColor}
                      onChange={e => { setDarkColor(e.target.value); setActiveColorPreset(''); }}
                      className="qr-color-input"
                    />
                    <span className="qr-color-hex">{darkColor.toUpperCase()}</span>
                  </div>
                </div>

                <div className="qr-color-pick">
                  <label className="qr-color-label">
                    Background
                    {transparent && <span className="qr-transparent-badge">Transparent</span>}
                  </label>
                  <div className="qr-color-input-wrap">
                    <input
                      type="color"
                      value={lightColor}
                      onChange={e => { setLightColor(e.target.value); setTransparent(false); setActiveColorPreset(''); }}
                      className="qr-color-input"
                      disabled={transparent}
                    />
                    <span className="qr-color-hex">{transparent ? 'None' : lightColor.toUpperCase()}</span>
                  </div>
                </div>

                <div className="qr-color-pick">
                  <label className="qr-color-label">Transparent BG</label>
                  <button
                    className={`qr-toggle-btn ${transparent ? 'qr-toggle-btn--on' : ''}`}
                    onClick={() => { setTransparent(t => !t); setActiveColorPreset(''); }}
                  >
                    {transparent ? 'On' : 'Off'}
                  </button>
                </div>
              </div>
            </div>

            {/* Size slider */}
            <div className="qr-control-group">
              <p className="qr-control-label">Export Size: {size} × {size}px</p>
              <input
                className="qr-slider"
                type="range"
                min={128}
                max={1024}
                step={64}
                value={size}
                onChange={e => setSize(Number(e.target.value))}
              />
              <div className="qr-slider-labels">
                <span>128px</span>
                <span>1024px</span>
              </div>
            </div>

            {selectedPreset.key === 'portfolio' && (
              <p className="qr-note">
                Auto-detected from current domain. Updates automatically when deployed.
              </p>
            )}

            <button
              className="qr-download-btn"
              onClick={handleDownload}
              disabled={!qrDataURL}
            >
              Export PNG
            </button>

          </div>

          {/* RIGHT -- QR preview */}
          <div className="qr-preview">
            {qrDataURL ? (
              <>
                <div
                  className="qr-frame"
                  style={{ background: transparent ? 'transparent' : lightColor }}
                >
                  {/* Checkerboard shows through when transparent */}
                  {transparent && <div className="qr-checkerboard" />}
                  <div className="qr-frame__corner qr-frame__corner--tl" />
                  <div className="qr-frame__corner qr-frame__corner--tr" />
                  <div className="qr-frame__corner qr-frame__corner--bl" />
                  <div className="qr-frame__corner qr-frame__corner--br" />
                  <img
                    src={qrDataURL}
                    alt="Generated QR Code"
                    className="qr-image"
                  />
                </div>
                <p className="qr-preview-label">{selectedPreset.label}</p>
                <p className="qr-preview-url">{activeURL}</p>
              </>
            ) : (
              <div className="qr-empty">
                {qrError
                  ? <p className="qr-error">⚠ {qrError}</p>
                  : <p>Enter a URL to generate</p>
                }
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="qr-footer">
          <HowItWorks content={HOW_IT_WORKS.qr} />
          <div className="qr-rule" />
          <p className="qr-footer-text">
            <AppLinks current="qr" />
          </p>
        </div>

      </div>
    </div>
  );
}